import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  Avatar,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import { useHistory } from "react-router-dom";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import flowchLogoHorizontal from "../assets/logo_principal_horizontal.png";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    minHeight: "100vh",
    height: "100dvh",
    backgroundColor:
      theme.palette.type === "dark" || theme.palette.mode === "dark"
        ? theme.palette.background.default
        : "#f1f3f4",
    [theme.breakpoints.down("sm")]: {
      minHeight: "100vh",
      height: "100dvh",
      backgroundColor: theme.palette.background.default,
    },
  },
  toolbar: {
    paddingLeft: 20,
    paddingRight: 24, // keep right padding when drawer closed
    [theme.breakpoints.down("sm")]: {
      paddingLeft: 8,
      paddingRight: 8,
    },
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    top: 0,
    right: 0,
    left: 0,
    width: "100%",
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor:
      theme.palette.type === "dark" || theme.palette.mode === "dark"
        ? theme.palette.background.default
        : "#f1f3f4",
    borderRadius: 0,
    border: "none",
    boxShadow: "none",
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      width: "100%",
    },
  },
  menuButton: {
    marginRight: 8,
    color: theme.palette.text.primary,
    [theme.breakpoints.down("sm")]: {
      marginLeft: 12,
      marginRight: 4,
    },
  },
  topLeftBrand: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
    flexShrink: 1,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(0.5),
    },
  },
  title: {
    flexGrow: 1,
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
  },
  topBrandLogo: {
    height: 30,
    width: "auto",
    maxWidth: 160,
    objectFit: "contain",
    [theme.breakpoints.down("sm")]: {
      height: 24,
      maxWidth: 120,
    },
  },
  drawerTopAction: {
    padding: theme.spacing(1.5, 2, 1),
    display: "flex",
    justifyContent: "flex-start",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5, 1),
      justifyContent: "center",
    },
  },
  drawerTopActionCollapsed: {
    justifyContent: "center",
    padding: theme.spacing(1.5, 0, 1),
  },
  newTicketButton: {
    borderRadius: 14,
    textTransform: "none",
    fontWeight: 600,
    padding: theme.spacing(1, 2.5),
    minWidth: 120,
    justifyContent: "flex-start",
    backgroundColor: "#c2e7ff",
    color: "#0b57d0",
    "&:hover": {
      backgroundColor: "#a9daf8",
    },
  },
  newTicketIcon: {
    marginRight: theme.spacing(1),
    fontSize: 22,
  },
  newTicketButtonCollapsed: {
    minWidth: 56,
    width: 56,
    height: 56,
    borderRadius: "50%",
    justifyContent: "center",
    padding: 0,
    "& $newTicketIcon": {
      marginRight: 0,
    },
  },
  newTicketButtonLabelCollapsed: {
    display: "none",
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor:
      theme.palette.type === "dark" || theme.palette.mode === "dark"
        ? theme.palette.background.default
        : "#f1f3f4",
    margin: 0,
    height: "100%",
    borderRadius: 0,
    border: "none",
    boxShadow: "none",
    [theme.breakpoints.down("sm")]: {
      margin: 0,
      height: "100%",
      borderRadius: 0,
      border: "none",
      boxShadow: "none",
    },
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  collapsedMenuList: {
    width: "100%",
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    "& li": {
      width: "100%",
      listStyle: "none",
      margin: 0,
      padding: 0,
    },
    "& .MuiBadge-root": {
      margin: "0 auto",
    },
    "& .MuiListSubheader-root": {
      display: "none",
    }
  },
  appBarSpacer: {
    minHeight: "48px",
  },
  content: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    padding: 0,
    borderTopLeftRadius: 18,
    backgroundColor:
      theme.palette.type === "dark" || theme.palette.mode === "dark"
        ? theme.palette.background.default
        : "#f1f3f4",
    [theme.breakpoints.down("sm")]: {
      padding: 0,
      borderTopLeftRadius: 0,
    },
  },
  contentBody: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  iconButton: {
    color: theme.palette.text.primary,
  },
  themeToggleButton: {
    color: theme.palette.text.primary,
    marginRight: theme.spacing(0.5),
  },
  themeIcon: {
    color: "inherit",
  },
  userAvatar: {
    width: 32,
    height: 32,
    fontSize: "0.8rem",
    fontWeight: 700,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

const getInitials = name =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join("")
    .toUpperCase();

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const { user } = useContext(AuthContext);
  const { toggleTheme } = useThemeContext();

  useEffect(() => {
    if (document.body.offsetWidth > 600) {
      setDrawerOpen(true);
    }
  }, []);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    handleCloseMenu();
    history.push("/profile");
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        onClose={drawerClose}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        ModalProps={{
          keepMounted: true,
        }}
        open={drawerOpen}
      >
        <div className={classes.appBarSpacer} />
        <List className={clsx(!drawerOpen && classes.collapsedMenuList)}>
          <MainListItems drawerClose={drawerClose} collapsed={!drawerOpen} />
        </List>
        {drawerOpen && <Divider />}
      </Drawer>
      <AppBar
        position="absolute"
        className={classes.appBar}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <div className={classes.topLeftBrand}>
            <IconButton
              edge="start"
              aria-label="open drawer"
              onClick={() => setDrawerOpen(!drawerOpen)}
              className={classes.menuButton}
            >
              <MenuIcon />
            </IconButton>
            <img src={flowchLogoHorizontal} alt="Flowch" className={classes.topBrandLogo} />
          </div>
          <Typography component="h1" variant="h6" noWrap className={classes.title} />

          <IconButton
            aria-label="alternar tema"
            onClick={toggleTheme}
            className={classes.themeToggleButton}
          >
            <Brightness4Icon className={classes.themeIcon} />
          </IconButton>

          {user.id && (
            <NotificationsPopOver className={classes.iconButton} />
          )}

          <div>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              className={classes.iconButton}
            >
              <Avatar
                src={user?.profilePicUrl}
                alt={user?.name || ""}
                className={classes.userAvatar}
              >
                {getInitials(user?.name)}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <div className={classes.contentBody}>{children ? children : null}</div>
      </main>
    </div>
  );
};

export default LoggedInLayout;
